import { useEffect, useRef, useState } from "react";
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react";
import type { components } from "@dolfin/api-admin";
import { useDolfinClient } from "@/components/DolfinProvider";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

type CountrySummary = components["schemas"]["CountryBankProfileSummary"];

export interface CountryComboboxProps {
  /** Selected ISO 3166-1 alpha-2 code (e.g. "IT"), or "" when nothing is chosen. */
  value: string;
  onChange: (code: string) => void;
  disabled?: boolean;
  id?: string;
  placeholder?: string;
  className?: string;
  /** Set when the picker sits inside a Dialog — see the note on `modal` below. */
  inDialog?: boolean;
}

// Backed by GET /v1/bank-details/countries, the list the organisation settings use, so the two can't drift.
export function CountryCombobox({
  value,
  onChange,
  disabled,
  id,
  placeholder = "Select country",
  className,
  inDialog,
}: CountryComboboxProps) {
  const api = useDolfinClient();
  const [countries, setCountries] = useState<CountrySummary[]>([]);
  const [open, setOpen] = useState(false);
  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    api.GET("/v1/bank-details/countries").then(({ data }) => {
      if (data) setCountries([...data].sort((a, b) => a.countryName.localeCompare(b.countryName)));
    });
  }, [api]);

  const selected = countries.find((c) => c.countryCode.toUpperCase() === value.toUpperCase());

  // modal inside a Dialog: the dialog's scroll lock otherwise swallows wheel events aimed at this
  // portaled list, and it can only be scrolled by dragging its scrollbar.
  return (
    <Popover open={open} onOpenChange={setOpen} modal={inDialog}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn("w-full justify-between font-normal", className)}
        >
          <span className={cn("truncate", !value && "text-muted-foreground")}>
            {value ? (selected ? `${selected.countryName} (${selected.countryCode})` : value) : placeholder}
          </span>
          <ChevronsUpDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) min-w-64 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search countries..." />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {value && (
                <CommandItem
                  value="__clear__"
                  onSelect={() => {
                    onChange("");
                    setOpen(false);
                  }}
                  className="text-muted-foreground"
                >
                  No country
                </CommandItem>
              )}
              {countries.map((c) => (
                <CommandItem
                  key={c.countryCode}
                  value={`${c.countryCode} ${c.countryName}`}
                  onSelect={() => {
                    onChange(c.countryCode);
                    setOpen(false);
                  }}
                >
                  <CheckIcon
                    className={cn(
                      "mr-2 size-4",
                      selected?.countryCode === c.countryCode ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {c.countryName}
                  <span className="ml-auto text-xs text-muted-foreground">{c.countryCode}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
